/**
 * Browser-API bridging helpers for byte buffers.
 *
 * The TypeScript lib.dom types model `Uint8Array` as a polymorphic
 * `Uint8Array<ArrayBufferLike>`, but Web Crypto and Blob construction
 * accept the narrower `BufferSource` / `BlobPart` interfaces. Casting at
 * the call site works but spreads `as unknown as` across the codebase
 * and makes the intent unclear at a glance.
 *
 * Centralise the bridge here so each call site is one named function.
 * If a future TypeScript update fixes the variance issue we delete the
 * helpers and the call sites need no further change.
 */

/** Pass a `Uint8Array` to the `Blob` constructor expecting a `BlobPart`. */
export function asBlobPart(bytes: Uint8Array): BlobPart {
  return bytes as unknown as BlobPart;
}

/**
 * Apply `String.fromCharCode` over a `Uint8Array` slice. Used by base64
 * encoders that build a binary string before `btoa`. Faster than a
 * char-by-char loop for slices up to a few thousand bytes.
 */
export function bytesToBinaryString(bytes: Uint8Array): string {
  return String.fromCharCode.apply(null, Array.from(bytes) as unknown as number[]);
}

/**
 * Bytes → a byte-per-code-unit string, chunked so it holds for a whole message
 * and not just a slice.
 *
 * {@link bytesToBinaryString} is the same mapping in one `apply` call and stays
 * as it is: base64 encoders hand it a few thousand bytes at a time and the
 * single call is faster. A whole `.eml` is not a slice, and `apply` over a
 * multi-megabyte array overflows the argument limit, so anything reading a
 * MESSAGE calls this one.
 *
 * This is the entry-point half of the invariant {@link byteStringToBytes}
 * spells out below: it is how octets from a `File`, a `fetch` response or a
 * decoder become the string the rest of the codebase treats as those octets.
 */
export function bytesToByteString(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let acc = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    acc += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return acc;
}

/**
 * Text a person typed → the byte string of its UTF-8 encoding.
 *
 * The one place a message enters this product WITHOUT already being bytes is
 * the home textarea: a `value` is a normal JS string, where a code unit is
 * UTF-16 and "ü" is one unit rather than the two octets a UTF-8 file holds.
 * Handing that to {@link byteStringToBytes} masks each code unit with
 * `& 0xff`, and the byte sequence that comes out exists nowhere: not on disk,
 * not as the preimage of any digest a reader could reproduce. Astral
 * characters are worse than truncated, because the mask runs on each surrogate
 * half separately.
 *
 * So pasted text has to be given an encoding, once, explicitly, and UTF-8 is
 * the only defensible choice: it is what the reader's editor would write if
 * they saved the same text as a `.eml`, so the SHA-256 the report prints is
 * reproducible with `sha256sum` against a file they can actually produce.
 * `packages/cli`'s `readSourceStdin` already treats piped text this way.
 *
 * Call this at the ENTRY POINT and once only. The encoded result is a byte
 * string, so a second call would encode it again ("Grüße" → "GrÃ¼ÃŸe" →
 * "GrÃƒÂ¼ÃƒÅ¸e"); `useInspect` keeps the editable draft and the encoded source
 * in separate fields precisely so that cannot happen.
 *
 * NOT for anything already read as bytes. A file, a `#import=` handoff and a
 * mailbox block are octets already, and re-encoding them corrupts exactly the
 * non-UTF-8 messages the byte invariant exists for.
 */
export function textToByteString(text: string): string {
  return bytesToByteString(new TextEncoder().encode(text));
}

/**
 * Inverse of {@link bytesToBinaryString}: map a byte-per-code-unit string
 * (binary / ISO-8859-1, where each char's code unit is one original octet
 * 0x00-0xFF) back to the exact octets via `charCodeAt(i) & 0xff`.
 *
 * This is the codebase's load-bearing byte invariant: raw email files are
 * read as byte-per-code-unit strings (see useFileLoader), so every downstream
 * string keeps `charCodeAt(i) === octet(i)`. Hashing/verifying such a string
 * MUST go through this helper — `new TextEncoder().encode(s)` would re-encode
 * any 0x80-0xFF char into a multi-byte UTF-8 sequence and corrupt the bytes
 * (a DKIM / S-MIME body-hash mismatch on any non-ASCII body).
 */
export function byteStringToBytes(input: string): Uint8Array {
  const bytes = new Uint8Array(input.length);
  for (let i = 0; i < input.length; i++) {
    bytes[i] = input.charCodeAt(i) & 0xff;
  }
  return bytes;
}

/**
 * How many octets a byte-per-code-unit string holds: `byteStringToBytes(s)
 * .length` without building the array.
 *
 * The body is `input.length` and that is the whole point. The invariant above
 * has a size half, and the size half is the one that gets re-derived by
 * whoever needs a number in a hurry: `new Blob([s]).size` and `new
 * TextEncoder().encode(s).length` both look like "the byte length of this
 * string" and both answer for a UTF-8 encoding of it, counting every
 * 0x80-0xFF octet twice. That inflated number is not the file on disk, not
 * the preimage of the SHA-256 the report prints beside it, and not the length
 * of anything the exports write.
 *
 * Six call sites across `src/next` reported message sizes that way until
 * 2026-08-28; a German 8bit message of 470 octets was shown as 494. Call this
 * instead, and `src/__tests__/byte-length-chokepoint.test.ts` keeps the
 * re-encoding out of the size path for good.
 */
export function byteStringLength(input: string): number {
  return input.length;
}
